<template>
  <component
    :is="href ? 'a' : 'button'"
    v-bind="$attrs"
    :href="href || undefined"
    :target="target || undefined"
    :rel="target === '_blank' ? 'noopener noreferrer' : undefined"
    :aria-label="ariaLabel || undefined"
    @click="onClick($event)"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    class="psui-el-button"
    :class="[getComponentClass, { hover: isHover && !disabled}, { disabled: disabled || loading }]"
  >
    <svg
      v-if="loading && showLoadingLeft"
      class="psui-animate-spin psui-text-black"
      :class="[loadingSizeClass, iconLeft ? 'icon-left-side' : 'psui-mr-2']"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        class="psui-opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"
      />
      <path
        class="psui-opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>

    <i
      v-else-if="iconLeft"
      :class="[iconLeftClass, 'icon-left-side']"
      class="material-icons-round"
      aria-hidden="true"
    >{{ iconLeft }}</i>
    
    <span v-if="label">{{ label }}</span>

    <svg
      v-if="loading && showLoadingRight"
      class="psui-animate-spin psui-text-black"
      :class="[loadingSizeClass, iconRight ? 'icon-right-side' : 'psui-ml-2']"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        class="psui-opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"
      />
      <path
        class="psui-opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>

    <i
      v-else-if="iconRight"
      :class="[iconRightClass, 'icon-right-side']"
      class="material-icons-round"
      aria-hidden="true"
    >{{ iconRight }}</i>
  </component>
</template>

<script setup>
import { computed, ref, } from 'vue'

const isHover = ref(false)

const emit = defineEmits(['click'])

const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  layout: {
    type: String,
    default: 'solid',
    validator: (value) => [
      'solid', 
      'outline', 
      // 'ghost',
      'onlytext', 
      'caution'
    ].includes(value),
  },
  href: {
    type: String,
    default: '',
  },
  target: {
    type: String,
    default: '',
  },
  ariaLabel: {
    type: String,
    default: '',
  },
  iconLeft: {
    type: String,
    default: '',
  },
  iconLeftClass: {
    type: String,
    default: '',
  },
  iconRight: {
    type: String,
    default: '',
  },
  iconRightClass: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'big',
    validator: (value) => ['compact', 'small', 'medium', 'big'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingPosition: {
    type: String,
    default: 'left',
    validator: (value) => ['left', 'right'].includes(value),
  },
})

const showLoadingLeft = computed(() => {
  if (props.iconLeft) return true
  if (!props.iconLeft && !props.iconRight && props.loadingPosition === 'left') return true
  return false
})

const showLoadingRight = computed(() => {
  if (props.iconRight) return true
  if (!props.iconLeft && !props.iconRight && props.loadingPosition === 'right') return true
  return false
})

const loadingSizeClass = computed(() => {
  const sizeMap = {
    compact: 'psui-h-3.5 psui-w-3.5',
    small: 'psui-h-4 psui-w-4',
    medium: 'psui-h-[18px] psui-w-[18px]',
    big: 'psui-h-5 psui-w-5',
  }
  return sizeMap[props.size] || sizeMap.big
})

const getComponentClass = computed(() => {
  const classes = [`layout-${props.layout}`, `size-${props.size}`]
  
  if (props.iconLeft && props.iconRight) {
    classes.push('icon-both')
  } else if (props.iconLeft) {
    classes.push('icon-left')
  } else if (props.iconRight) {
    classes.push('icon-right')
  }
  
  return classes.join(' ')
})

const onClick = (event) => {
  if (props.disabled) return
  emit('click', event)
}

const onMouseEnter = () => {
  isHover.value = true
}

const onMouseLeave = () => {
    isHover.value = false
}
</script>
